import type { ChatMessageType } from "@/api/chat";

/** A message we appended optimistically before the server echoed it back. */
export type OptimisticFlag = { _optimistic?: boolean };

/**
 * A message as it arrives over the agent WebSocket. It's a ChatMessageType plus
 * the server's `type` discriminator ("chat_message" | "event"), which the shared
 * ChatMessageType interface doesn't carry.
 */
export type IncomingAgentMessage = ChatMessageType &
  OptimisticFlag & { type?: string };

/**
 * Fold an incoming WebSocket message into the current list, returning the next
 * list. Pure so the tricky cases can be unit-tested without a live socket:
 *  - optimistic echo dedup: the server's echo of the user's own message
 *    replaces the placeholder we added in sendMessage (no double bubble);
 *  - id dedup: a message already present (same id) is not appended twice. Note
 *    the caller already drops duplicate chat_messages via its processed-id
 *    guard, so this branch mainly covers event frames;
 *  - **New-chat race**: when suppressAiResponse is set, a stale in-flight AI
 *    reply (a chat_message with no user id) is dropped instead of repopulating
 *    a conversation the user already cleared. The caller arms/disarms that flag
 *    via its own ref — this function just honours it.
 *
 * An AI/system message is identified by the absence of a user id (the assistant
 * sends user:{id:null}); the user's own echo carries their id.
 */
export function applyIncomingAgentMessage(
  messages: ChatMessageType[],
  incoming: IncomingAgentMessage,
  opts: { suppressAiResponse: boolean },
): ChatMessageType[] {
  const isChat = incoming.type === "chat_message";
  const isAiMessage = isChat && !incoming.user?.id;

  if (opts.suppressAiResponse && isAiMessage) {
    // Stale in-flight reply from a conversation the user already cleared.
    return messages;
  }

  let base = messages;
  if (incoming.sender === "human") {
    base = messages.filter(
      (m) =>
        !(
          (m as ChatMessageType & OptimisticFlag)._optimistic &&
          m.content === incoming.content
        ),
    );
  }

  const exists = base.some((m) => m.id && incoming.id && m.id === incoming.id);
  if (exists) return base;
  return [...base, incoming];
}
